// ...existing code...
import React, { useEffect, useMemo, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import {
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Box,
	Button,
	Checkbox,
	MenuItem,
	Popover,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/es";
import dayjs from "dayjs";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useTranslation } from "react-i18next";
import customParseFormat from "dayjs/plugin/customParseFormat";

import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
dayjs.extend(isSameOrBefore);

dayjs.extend(customParseFormat);
const toDayjsFromHHmm = (s) => (s ? dayjs(s, "HH:mm", true) : null);
const toHH00 = (v) => (v ? dayjs(v).format("HH:00") : "");
const isAfterHH = (a, b) => (a && b ? a > b : false); // strings "HH:00" con padding comparan bien
const clampTimes = (s, e) => (isAfterHH(s, e) ? [s, s] : [s, e]);

const HOURS = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, "0")}:00`);
const LEFT_COL_PX = 220;
const isMonthKey = (k) => /^\d{4}[-_]\d{2}$/.test(k);

const toISO = (s) => {
	if (!s) return null;
	const d = dayjs(s, ["YYYY-MM-DD", "DD/MM/YYYY", "MM/DD/YYYY"], true);
	return d.isValid() ? d.format("YYYY-MM-DD") : null;
};

const normalizeDayKeys = (block) => {
	const out = {};
	for (const k of Object.keys(block || {})) {
		// preservar metadatos
		if (k === "inicio" || k === "fin" || k === "start" || k === "end") {
			out[k] = block[k];
			continue;
		}
		// Si parece una fecha, normalizar a DMY con ceros
		const iso = toISO(k);
		if (iso) {
			const kDMY = dayjs(iso).format("DD/MM/YYYY");
			out[kDMY] = block[k];
		} else {
			// no es fecha => copiar tal cual (por si hay otros campos)
			out[k] = block[k];
		}
	}
	return out;
};
const getHorasForDay = (block, iso) => {
	const d = dayjs(iso);
	const candidates = [
		d.format("DD/MM/YYYY"),
		d.format("D/M/YYYY"),
		d.format("YYYY-MM-DD"),
		d.format("DD-MM-YYYY"),
		d.format("MM/DD/YYYY"),
	];
	for (const key of candidates) {
		const v = block[key];
		if (v && typeof v === "object") return v;
	}
	return {};
};

const cellCommon = {
	borderRight: "1px solid",
	borderBottom: "1px solid",
	borderColor: "divider",
	textAlign: "center",
	px: 0.5,
	py: 0.5,
	fontVariantNumeric: "tabular-nums",
	whiteSpace: "nowrap",
};

const cellHeader = { ...cellCommon, fontWeight: 600, bgcolor: "background.default" };

const cellLeft = {
	...cellCommon,
	textAlign: "left",
	position: "sticky",
	left: 0,
	zIndex: 2,
	bgcolor: "background.paper",
};

function normalizeData(json) {
	if (!json) return { months: [], map: {} };

	const map = {};
	Object.keys(json)
		.filter(isMonthKey)
		.forEach((rawKey) => {
			const blockKey = rawKey.replace("_", "-"); // normaliza a YYYY-MM
			const rawBlock = json[blockKey] || {};
			const block = normalizeDayKeys(rawBlock); // <= normalizamos claves de días

			const inicio = block.inicio ?? block.start;
			const fin = block.fin ?? block.end;

			const startISO = toISO(inicio);
			const endISO = toISO(fin);
			const startDate = dayjs(startISO);
			const endDate = dayjs(endISO);

			if (!startDate.isValid() || !endDate.isValid() || startDate.isAfter(endDate)) {
				// si algo no cierra, deja el mes vacío pero explícito
				map[blockKey] = [];
				return;
			}

			const dias = [];
			let d = startDate.clone();
			while (d.isSameOrBefore(endDate, "day")) {
				dias.push(d.format("YYYY-MM-DD"));
				d = d.add(1, "day");
			}

			map[blockKey] = dias.map((iso) => {
				const horas = getHorasForDay(block, iso);
				return {
					ddmmyyyy: dayjs(iso).format("DD/MM/YYYY"),
					iso,
					horas: { ...horas },
					selected: false,
					selectedHours: new Set(),
					hasData: Object.keys(horas).length > 0,
				};
			});

			// (Opcional) guardar metadatos por si luego necesitas rearmar el payload tal cual vino
			map[blockKey]._meta = {
				inicio: inicio,
				fin: fin,
				dayKeyFormat: "DD/MM/YYYY",
			};
		});

	Object.keys(map).forEach((m) => {
		map[m] = (map[m] || []).filter((r) => r && r.iso); // descartar _meta
		map[m].sort((a, b) => a.iso.localeCompare(b.iso));
	});

	const months = Object.keys(map).sort();
	return { months, map };
}
// --- Sanitizador: redondea y trunca a mínimo 0
const sanitizeInt = (v) => {
	const n = Number(v);
	if (!Number.isFinite(n)) return 0;
	return Math.max(0, Math.round(n));
};

function buildPayload({ data, months, map }) {
	const result = { origen: data?.origen ?? "DB" };
	months.forEach((month) => {
		const rows = map[month] || [];
		// inicio/fin desde el json original (más confiable que _meta)
		const raw = data?.[month] || {};
		const inicio = raw.inicio ?? raw.start ?? null;
		const fin = raw.fin ?? raw.end ?? null;

		if (!result[month]) result[month] = {};
		if (inicio) result[month].inicio = inicio;
		if (fin) result[month].fin = fin;

		rows.forEach((row) => {
			const dayKey = dayjs(row.iso).format("DD/MM/YYYY");
			const clean = {};
			Object.entries(row.horas || {}).forEach(([h, v]) => {
				clean[h] = sanitizeInt(v);
			});
			result[month][dayKey] = clean;
		});
	});
	return result;
}

const buildFiltered = (y, sd, ed, monthsParam, mapParam) =>
	monthsParam.map((m) => {
		// Convertir filtros a formato YYYY-MM-DD para comparar
		const sdISO = sd ? toISO(dayjs(sd).format("DD/MM/YYYY")) : "";
		const edISO = ed ? toISO(dayjs(ed).format("DD/MM/YYYY")) : "";
		const rows = (mapParam[m] || []).filter(
			(r) => (!sdISO || r.iso >= sdISO) && (!edISO || r.iso <= edISO)
		);
		return { month: m, rows };
	});

// -------- Componente --------
export default function ForecastStepTwo({
	data, // JSON de create/get
	mode = "create", // "create" | "edit"
	selectedMonth = null, // mes YYYY-MM a mostrar (ej: "2025-03")
	onApplyFilters, // opcional
	onSave, // callback con {filtered, months, map, filter}
	onBack, // solo create
	saving = false,
}) {
	const { t } = useTranslation();

	// Estado fuente y vista
	const [months, setMonths] = useState([]);
	const [map, setMap] = useState({});
	const [filtered, setFiltered] = useState([]);
	const [loading, setLoading] = useState(true);

	// Filtros UI
	const nowYearFallback = useMemo(
		() => (months[0] ? Number(months[0].slice(0, 4)) : dayjs().year()),
		[months]
	);
	const [year, setYear] = useState(nowYearFallback);
	const [startDate, setStartDate] = useState(""); // YYYY-MM-DD
	const [endDate, setEndDate] = useState(""); // YYYY-MM-DD
	const [startTime, setStartTime] = useState("00:00");
	const [endTime, setEndTime] = useState("23:00");
	const fullDay = false;
	const dense = true;

	// Filtro APLICADO (lo que realmente rige la vista)
	const [appliedFilter, setAppliedFilter] = useState({ year: null, startDate: "", endDate: "" });

	const [selectedColumnsByMonth, setSelectedColumnsByMonth] = useState({});

	const visibleHours = useMemo(() => {
		return HOURS.filter((h) => h >= startTime && h <= endTime);
	}, [startTime, endTime]);

	const gridStyle = useMemo(
		() => ({
			display: "grid",
			gridTemplateColumns: `${LEFT_COL_PX}px repeat(${visibleHours.length}, minmax(28px, 1fr))`,
			alignItems: "stretch",
			fontSize: dense ? 12 : 14,
			width: "100%",
		}),
		[visibleHours.length, dense]
	);

	// --- Cargar / Normalizar data
	useEffect(() => {
		setLoading(true);
		let norm = normalizeData(data);
		// Si hay mes seleccionado, filtrar solo ese mes
		if (selectedMonth && norm.months.includes(selectedMonth)) {
			norm = {
				months: [selectedMonth],
				map: { [selectedMonth]: norm.map[selectedMonth] },
			};
		}
		setMonths(norm.months);
		setMap(norm.map);
		setLoading(false);
	}, [data, selectedMonth]);

	useEffect(() => {
		const y = appliedFilter.year ?? (months[0] ? Number(months[0].slice(0, 4)) : dayjs().year());
		const sd = appliedFilter.startDate || "";
		const ed = appliedFilter.endDate || "";

		setFiltered((prev) => {
			const resAll = buildFiltered(y, sd, ed, months, map);
			const isDateFiltered = Boolean(sd || ed);
			const next = isDateFiltered ? resAll.filter((g) => g.rows.length > 0) : resAll;

			// Reaplicar selección previa por month+iso
			const prevIndex = new Map(prev.map((pg) => [pg.month, new Map(pg.rows.map((r) => [r.iso, r]))]));

			return next.map((group) => {
				const byIso = prevIndex.get(group.month);
				if (!byIso) return group;
				return {
					...group,
					rows: group.rows.map((r) => {
						const prevRow = byIso.get(r.iso);
						return prevRow
							? { ...r, selected: prevRow.selected, selectedHours: prevRow.selectedHours }
							: r;
					}),
				};
			});
		});
	}, [months, map, appliedFilter]);

	// --- Selecciones / Ediciones
	const toggleHour = (m, iso, hour) => {
		setFiltered((prev) =>
			prev.map((group) => ({
				...group,
				rows: group.rows.map((r) => {
					if (group.month !== m || r.iso !== iso) return { ...r, selectedHours: new Set() };
					const sel = new Set(r.selectedHours || []);
					if (sel.has(hour)) sel.delete(hour);
					else {
						sel.clear();
						sel.add(hour);
					}
					return { ...r, selectedHours: sel };
				}),
			}))
		);
	};

	const changeHourValue = (m, iso, hour, value) => {
		const next = value === "" ? "" : sanitizeInt(value);
		setMap((prev) => {
			const rows = prev[m] || [];
			const newRows = rows.map((r) =>
				r.iso === iso ? { ...r, horas: { ...r.horas, [hour]: next } } : r
			);
			return { ...prev, [m]: newRows };
		});

		setFiltered((prev) =>
			prev.map((g) =>
				g.month !== m
					? g
					: {
							...g,
							rows: g.rows.map((r) =>
								r.iso === iso
									? {
											...r,
											horas: { ...r.horas, [hour]: next },

											selectedHours: r.selectedHours,
									  }
									: r
							),
					  }
			)
		);
	};

	const toggleRow = (m, iso, checked) => {
		setFiltered((prev) =>
			prev.map((group) =>
				group.month !== m
					? group
					: { ...group, rows: group.rows.map((r) => (r.iso === iso ? { ...r, selected: checked } : r)) }
			)
		);
	};

	const setMonthChecked = (mKey, checked) => {
		setFiltered((prev) =>
			prev.map((g) =>
				g.month !== mKey ? g : { ...g, rows: g.rows.map((r) => ({ ...r, selected: checked })) }
			)
		);
	};

	const setAllMonthsChecked = (checked) => {
		setFiltered((prev) =>
			prev.map((g) => ({ ...g, rows: g.rows.map((r) => ({ ...r, selected: checked })) }))
		);
	};

	const toggleColumn = (monthKey, hour) => {
		setSelectedColumnsByMonth((prev) => {
			const next = { ...prev };
			const set = new Set(next[monthKey] || []);
			set.has(hour) ? set.delete(hour) : set.add(hour);
			next[monthKey] = set;
			return next;
		});
	};

	// --- Filtros (acciones)
	const handleApplyFilters = (opts = {}) => {
		const y = opts.year ?? year;
		const sd = opts.startDate ?? startDate;
		const ed = opts.endDate ?? endDate;

		setAppliedFilter({ year: y, startDate: sd, endDate: ed });

		const resAll = buildFiltered(y, sd, ed, months, map);
		const isDateFiltered = Boolean(sd || ed);
		setFiltered(isDateFiltered ? resAll.filter((g) => g.rows.length > 0) : resAll);

		onApplyFilters?.({ year: y, startDate: sd, endDate: ed, startTime, endTime, fullDay });
	};

	const onChangeYear = (y) => {
		setYear(y);
		handleApplyFilters({ year: y });
	};
	const onChangeStartDate = (v) => {
		setStartDate(v);
		handleApplyFilters({ startDate: v });
	};
	const onChangeEndDate = (v) => {
		setEndDate(v);
		handleApplyFilters({ endDate: v });
	};

	const handleClearFilters = () => {
		const defaults = {
			year: months[0] ? Number(months[0].slice(0, 4)) : dayjs().year(),
			startDate: "",
			endDate: "",
		};
		setYear(defaults.year);
		setStartDate(defaults.startDate);
		setEndDate(defaults.endDate);
		setStartTime("00:00");
		setEndTime("23:00");
		setSelectedColumnsByMonth({});
		setAppliedFilter(defaults);

		const resAll = buildFiltered(defaults.year, "", "", months, map);
		setFiltered(resAll);
	};

	// --- Operaciones en bloque
	const [opAnchor, setOpAnchor] = useState(null);
	const [opType, setOpType] = useState("+"); // + | - | * | /
	const [opValue, setOpValue] = useState("");
	const [opIsPercent, setOpIsPercent] = useState(false); // Nuevo: modo porcentaje

	const openOp = (e) => setOpAnchor(e.currentTarget);
	const closeOp = () => setOpAnchor(null);

	const applyOperation = () => {
		const v = Number(opValue);
		if (Number.isNaN(v)) return;

		const [hs, he] = fullDay ? ["00:00", "23:00"] : [startTime, endTime];
		const rangeDefault = HOURS.filter((h) => h >= hs && h <= he);

		setMap((prev) => {
			const next = { ...prev };

			filtered.forEach((group) => {
				const monthCols = selectedColumnsByMonth[group.month] || new Set();
				const hasColSel = monthCols.size > 0;
				const hasRowSel = group.rows.some((r) => r.selected);

				// Usar siempre group.month como key
				const rows = next[group.month] ? [...next[group.month]] : [];

				group.rows.forEach((r) => {
					const hoursSet = new Set();
					if (hasColSel) Array.from(monthCols).forEach((h) => hoursSet.add(h));
					if (hasRowSel && r.selected) rangeDefault.forEach((h) => hoursSet.add(h));
					if (!hasRowSel && !hasColSel && r.selectedHours && r.selectedHours.size > 0) {
						r.selectedHours.forEach((h) => hoursSet.add(h));
					}
					if (hoursSet.size === 0) return;

					const idx = rows.findIndex((x) => x.iso === r.iso);
					if (idx < 0) return;

					const row = rows[idx];
					const newHoras = { ...row.horas };
					hoursSet.forEach((h) => {
						const cur = Number(newHoras[h] ?? 0);
						let nextVal = cur;
						if (opIsPercent) {
							// Operación con porcentaje
							if (opType === "+") nextVal = cur + (cur * v) / 100;
							if (opType === "-") nextVal = cur - (cur * v) / 100;
							if (opType === "*") nextVal = cur * (v / 100);
							if (opType === "/") nextVal = v === 0 ? cur : cur / (v / 100);
						} else {
							// Operación normal
							if (opType === "+") nextVal = cur + v;
							if (opType === "-") nextVal = cur - v;
							if (opType === "*") nextVal = cur * v;
							if (opType === "/") nextVal = v === 0 ? cur : cur / v;
						}
						newHoras[h] = Number.isFinite(nextVal) ? sanitizeInt(nextVal) : cur;
					});

					rows[idx] = { ...row, horas: newHoras };
				});
				next[group.month] = rows;
			});

			// Mantener vista bajo el filtro aplicado (sin resetearlo)
			const y = appliedFilter.year ?? year;
			const sd = appliedFilter.startDate || "";
			const ed = appliedFilter.endDate || "";
			const resAll = buildFiltered(y, sd, ed, months, next);
			const isDateFiltered = Boolean(sd || ed);
			setFiltered(isDateFiltered ? resAll.filter((g) => g.rows.length > 0) : resAll);

			return next;
		});

		// Limpia selección puntual de celdas
		setFiltered((prev) =>
			prev.map((g) => ({ ...g, rows: g.rows.map((r) => ({ ...r, selectedHours: new Set() })) }))
		);

		closeOp();
	};

	const handleStartTimeChange = (v) => {
		const s = toHH00(v);
		const [ns, ne] = clampTimes(s, endTime);
		setStartTime(ns);
		if (ne !== endTime) setEndTime(ne);
	};
	const handleEndTimeChange = (v) => {
		const e = toHH00(v);
		const [ns, ne] = clampTimes(startTime, e);
		if (ns !== startTime) setStartTime(ns);
		setEndTime(ne);
	};

	// --- Guardar
	const handleSave = () => {
		const result = buildPayload({ data, months, map });
		onSave?.(result);
	};

	// Obtener todos los años presentes en los meses de la data
	const yearsAvailable = useMemo(
		() => Array.from(new Set(months.map((m) => m.slice(0, 4)))).sort(),
		[months]
	);

	// Días disponibles en ISO (YYYY-MM-DD) — requerido por <input type="date">
	const availableDaysISO = useMemo(() => {
		const days = [];
		months.forEach((month) => {
			(map[month] || []).forEach((row) => {
				days.push(row.iso); // ya en ISO
			});
		});
		return days.sort((a, b) => a.localeCompare(b));
	}, [months, map]);

	// -------- UI --------
	return (
		<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "stretch",
					width: "100%",
					height: "100%",
					minHeight: 0, // clave para que el interior pueda scrollear
				}}
			>
				{loading ? (
					<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
						<CircularProgress />
					</Box>
				) : (
					<>
						{/* Filtros */}
						<Stack
							direction="row"
							spacing={2}
							alignItems="center"
							sx={{ mb: 2, flexWrap: "wrap", gap: 2, mt: 2 }}
						>
							<TextField
								select
								size="small"
								label={t("forecast.filters.year")}
								value={year}
								onChange={(e) => onChangeYear(Number(e.target.value))}
								sx={{ width: 120 }}
							>
								{yearsAvailable.map((y) => (
									<MenuItem key={y} value={Number(y)}>
										{y}
									</MenuItem>
								))}
							</TextField>

							<TextField
								label={t("forecast.filters.startDate")}
								type="date"
								size="small"
								value={startDate}
								onChange={(e) => onChangeStartDate(e.target.value)}
								sx={{ width: 180 }}
								InputLabelProps={{ shrink: true }}
								inputProps={{
									min: availableDaysISO[0] || undefined,
									max: availableDaysISO[availableDaysISO.length - 1] || undefined,
								}}
							/>

							<TextField
								label={t("forecast.filters.endDate")}
								type="date"
								size="small"
								value={endDate}
								onChange={(e) => onChangeEndDate(e.target.value)}
								sx={{ width: 180 }}
								InputLabelProps={{ shrink: true }}
								inputProps={{
									min: availableDaysISO[0] || undefined,
									max: availableDaysISO[availableDaysISO.length - 1] || undefined,
								}}
							/>

							<TimePicker
								label={t("forecast.filters.startTime")}
								value={toDayjsFromHHmm(startTime)}
								onChange={handleStartTimeChange}
								ampm={false}
								views={["hours", "minutes"]}
								inputFormat="HH:mm"
								mask="__:__"
								minutesStep={60}
								maxTime={toDayjsFromHHmm(endTime)}
								enableAccessibleFieldDOMStructure={false}
								slotProps={{
									// estilos del TextField interno
									textField: { size: "small", sx: { width: 160 } },
								}}
							/>

							<TimePicker
								label={t("forecast.filters.endTime")}
								value={toDayjsFromHHmm(endTime)}
								onChange={handleEndTimeChange}
								views={["hours", "minutes"]}
								inputFormat="HH:mm"
								mask="__:__"
								minutesStep={60}
								enableAccessibleFieldDOMStructure={false}
								ampm={false}
								minTime={toDayjsFromHHmm(startTime)}
								slotProps={{
									textField: { size: "small", sx: { width: 160 } },
								}}
							/>

							<Button onClick={handleClearFilters}>{t("forecast.filters.clear")}</Button>

							<Box flex={1} />
							<Button startIcon={<MoreVertIcon />} variant="outlined" onClick={openOp}>
								{t("forecast.table.operation")}
							</Button>
							<Popover
								open={!!opAnchor}
								anchorEl={opAnchor}
								onClose={closeOp}
								anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
								PaperProps={{
									sx: { p: 2, minWidth: 220, maxWidth: 320, width: "100%" },
								}}
							>
								<Box sx={{ width: "100%" }}>
									<Typography variant="subtitle2" gutterBottom>
										{t("forecast.table.operation")}
									</Typography>
									<Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
										<TextField
											size="small"
											fullWidth
											label={t("forecast.table.value")}
											value={opValue}
											onChange={(e) => setOpValue(e.target.value.replace(/[^\d.]/g, ""))}
											inputProps={{ inputMode: "decimal" }}
										/>
										<Button
											variant={opIsPercent ? "contained" : "outlined"}
											onClick={() => setOpIsPercent((v) => !v)}
											sx={{ ml: 1, minWidth: 36, height: 40 }}
											title={opIsPercent ? "Modo porcentaje activado" : "Aplicar como porcentaje"}
										>
											%
										</Button>
									</Box>
									<Stack direction="row" spacing={1} sx={{ mt: 1, width: "100%" }}>
										<Button
											variant={opType === "+" ? "contained" : "outlined"}
											onClick={() => setOpType("+")}
											sx={{ flex: 1, minWidth: 0 }}
										>
											+
										</Button>
										<Button
											variant={opType === "-" ? "contained" : "outlined"}
											onClick={() => setOpType("-")}
											sx={{ flex: 1, minWidth: 0 }}
										>
											-
										</Button>
										<Button
											variant={opType === "*" ? "contained" : "outlined"}
											onClick={() => setOpType("*")}
											sx={{ flex: 1, minWidth: 0 }}
										>
											×
										</Button>
										<Button
											variant={opType === "/" ? "contained" : "outlined"}
											onClick={() => setOpType("/")}
											sx={{ flex: 1, minWidth: 0 }}
										>
											÷
										</Button>
									</Stack>
									<Button fullWidth sx={{ mt: 1 }} variant="contained" onClick={applyOperation}>
										{t("forecast.table.saveChanges")}
									</Button>
								</Box>
							</Popover>
						</Stack>

						{/* Lista de meses con checkbox GENERAL en el header */}
						{filtered.length === 0 ? (
							<Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
								<Typography color="text.secondary">Sin resultados para los filtros aplicados.</Typography>
							</Box>
						) : (
							<Box
								sx={{
									border: 1,
									borderColor: "divider",
									borderRadius: 1,
									px: 2,
									py: 1,
									overflowX: "hidden",
									overflowY: "auto",
									flex: 1,
									minHeight: 0,
									display: "block",
								}}
							>
								<Accordion defaultExpanded disableGutters>
									<AccordionSummary expandIcon={<ExpandMoreIcon />}>
										{(() => {
											const totalRows = filtered.reduce(
												(acc, g) => acc + g.rows.filter((r) => Object.keys(r.horas).length > 0).length,
												0
											);
											const selectedRows = filtered.reduce(
												(acc, g) =>
													acc + g.rows.filter((r) => r.selected && Object.keys(r.horas).length > 0).length,
												0
											);
											const all = totalRows > 0 && selectedRows === totalRows;
											const any = selectedRows > 0 && selectedRows < totalRows;
											return (
												<Stack direction="row" alignItems="center" spacing={1}>
													<Checkbox
														size="small"
														checked={all}
														indeterminate={!all && any}
														onChange={(e) => setAllMonthsChecked(e.target.checked)}
														sx={{ mr: 1 }}
													/>
													<Typography fontWeight={700}>Meses</Typography>
												</Stack>
											);
										})()}
									</AccordionSummary>

									<AccordionDetails sx={{ p: 0 }}>
										{filtered.map((group) => {
											// Solo mostrar filas con horas no vacías
											const validRows = group.rows.filter((r) => Object.keys(r.horas).length > 0);
											const allChecked = validRows.length > 0 && validRows.every((r) => r.selected);
											const anyChecked = validRows.some((r) => r.selected) && !allChecked;
											const selectedCount = validRows.filter((r) => r.selected).length;

											return (
												<Accordion key={group.month} defaultExpanded disableGutters>
													<AccordionSummary expandIcon={<ExpandMoreIcon />}>
														{/* Checkbox del MES */}
														<Checkbox
															size="small"
															checked={allChecked}
															indeterminate={anyChecked}
															onChange={(e) => setMonthChecked(group.month, e.target.checked)}
															sx={{ mr: 1 }}
														/>
														<Typography fontWeight={600}>
															{group.month} {selectedCount ? `(${selectedCount})` : ""}
														</Typography>
													</AccordionSummary>

													<AccordionDetails sx={{ p: 0 }}>
														{validRows.length === 0 ? (
															<Box sx={{ p: 2 }}>
																<Typography color="text.secondary">
																	{t("forecast.month.noData", "No hay datos disponibles para este mes")}
																</Typography>
															</Box>
														) : (
															<Box sx={{ ...gridStyle, margin: "0" }}>
																{/* Encabezado izquierda */}
																<Box sx={{ ...cellHeader, ...cellLeft }}>{t("forecast.table.monthDay")}</Box>

																{/* Encabezados de horas (selección de columna por MES) */}
																{visibleHours.map((h) => {
																	const monthCols = selectedColumnsByMonth[group.month] || new Set();
																	const isSel = monthCols.has(h);
																	return (
																		<Box
																			key={`h-${h}`}
																			sx={{
																				...cellHeader,
																				cursor: "pointer",
																				bgcolor: isSel ? "primary.50" : cellHeader.bgcolor,
																				borderBottom: "2px solid",
																				borderColor: isSel ? "primary.main" : "divider",
																				userSelect: "none",
																			}}
																			onClick={() => toggleColumn(group.month, h)}
																			title={isSel ? "Quitar selección de columna" : "Seleccionar columna"}
																		>
																			{h}
																		</Box>
																	);
																})}

																{/* Filas (días) */}
																{validRows.map((r) => (
																	<React.Fragment key={r.iso}>
																		<Box
																			sx={{
																				...cellLeft,
																				display: "flex",
																				alignItems: "center",
																				gap: 0.75,
																				bgcolor: r.selected ? "grey.50" : "background.paper",
																			}}
																		>
																			<Checkbox
																				size="small"
																				checked={!!r.selected}
																				onChange={(e) => toggleRow(group.month, r.iso, e.target.checked)}
																			/>
																			<Typography>{dayjs(r.iso).format("DD/MM/YYYY")}</Typography>
																		</Box>

																		{visibleHours.map((h) => {
																			const isSelectedHour = r.selectedHours?.has(h);
																			const monthCols = selectedColumnsByMonth[group.month] || new Set();
																			const colSelectedHere = monthCols.has(h);

																			const bg = isSelectedHour
																				? "primary.50"
																				: colSelectedHere
																				? "primary.50"
																				: r.selected
																				? "grey.50"
																				: "background.paper";

																			const val = r.horas[h] ?? "";
																			return (
																				<Box
																					key={`${r.iso}-${h}`}
																					sx={{ ...cellCommon, bgcolor: bg, cursor: "pointer" }}
																					onClick={() => toggleHour(group.month, r.iso, h)}
																				>
																					{isSelectedHour ? (
																						<TextField
																							size="small"
																							autoFocus
																							value={String(val ?? "")}
																							onChange={(e) => changeHourValue(group.month, r.iso, h, e.target.value)}
																							variant="standard"
																							inputProps={{
																								inputMode: "numeric",
																								pattern: "[0-9]*",
																								style: { textAlign: "center", width: "100%", maxWidth: "100%" },
																							}}
																							onMouseDown={(e) => e.stopPropagation()}
																							onClick={(e) => e.stopPropagation()}
																							onFocus={(e) => e.stopPropagation()}
																							onKeyDown={(e) => e.stopPropagation()}
																						/>
																					) : (
																						val
																					)}
																				</Box>
																			);
																		})}
																	</React.Fragment>
																))}
															</Box>
														)}
													</AccordionDetails>
												</Accordion>
											);
										})}
									</AccordionDetails>
								</Accordion>
							</Box>
						)}
						{/* Footer */}
						<Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
							{mode === "create" ? <Button onClick={onBack}>{t("forecast.modal.back")}</Button> : <span />}
							<LoadingButton variant="contained" onClick={handleSave} loading={saving}>
								{t("forecast.modal.save")}
							</LoadingButton>
						</Stack>
					</>
				)}
			</Box>
		</LocalizationProvider>
	);
}
